import { useMemo, useState } from "react";
import { MapPin, Search, X } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { ListingsMap, type ListingMapPoint } from "@/components/ListingsMap";

/* Brand loader for the market explorer: the gold map pin dropping over radar
   ripples — shown while a pin set is still in flight so the panel never reads
   "0 listings" for a market that simply hasn't answered yet. */
function MarketLoader({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10">
      <div className="relative flex h-16 w-16 items-center justify-center">
        <span className="absolute h-full w-full animate-ping rounded-full bg-primary/15" />
        <span className="absolute h-10 w-10 animate-ping rounded-full bg-primary/25 [animation-delay:200ms]" />
        <span className="absolute h-5 w-5 rounded-full bg-primary/20" />
        <MapPin className="relative h-7 w-7 animate-bounce text-primary drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)]" />
      </div>
      <p className="text-sm font-semibold tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

/* Full state name (as returned in map-points) → 2-letter abbreviation.
   Exported so pages can normalise their own data into ListingMapPoints. */
export const NAME_TO_ABBR: Record<string, string> = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA", Colorado: "CO",
  Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA", Hawaii: "HI", Idaho: "ID",
  Illinois: "IL", Indiana: "IN", Iowa: "IA", Kansas: "KS", Kentucky: "KY", Louisiana: "LA",
  Maine: "ME", Maryland: "MD", Massachusetts: "MA", Michigan: "MI", Minnesota: "MN",
  Mississippi: "MS", Missouri: "MO", Montana: "MT", Nebraska: "NE", Nevada: "NV",
  "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
  "North Carolina": "NC", "North Dakota": "ND", Ohio: "OH", Oklahoma: "OK", Oregon: "OR",
  Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD",
  Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT", Virginia: "VA", Washington: "WA",
  "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY",
};
export const ABBR_TO_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(NAME_TO_ABBR).map(([name, abbr]) => [abbr, name]),
);

const fmtPrice = (n: number | null): string => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

const abbrOf = (p: ListingMapPoint): string | null =>
  p.state_name ? NAME_TO_ABBR[p.state_name] ?? null : null;

export function MarketExplorer({
  points,
  onView,
  noun = "listings",
  countLabel = "active listings",
  comingSoonPoints,
  onViewComingSoon,
  loading = false,
  comingSoonLoading = false,
}: {
  points: ListingMapPoint[];
  onView: (id: string) => void;
  /** Plural noun for the items on the map (e.g. "deals" on Coming Soon). */
  noun?: string;
  /** Label under the big count in the markets panel. */
  countLabel?: string;
  /** When provided, renders an Active / Coming Soon toggle that swaps the pins. */
  comingSoonPoints?: ListingMapPoint[];
  /** Click handler for coming-soon items (falls back to onView). */
  onViewComingSoon?: (id: string) => void;
  /** True while the active pin set is still being fetched (cold load). */
  loading?: boolean;
  /** True while the coming-soon pin set is still being fetched. */
  comingSoonLoading?: boolean;
}) {
  const [query, setQuery] = useState("");
  // Which pin set the map shows. The toggle renders only when the caller
  // supplies coming-soon points.
  const [mapTab, setMapTab] = useState<"active" | "coming_soon">("active");
  const showToggle = comingSoonPoints != null;
  const comingSoonShown = showToggle && mapTab === "coming_soon";

  const shownPoints = comingSoonShown ? comingSoonPoints! : points;
  const shownNoun = comingSoonShown ? "deals" : noun;
  const shownCountLabel = comingSoonShown ? "coming soon deals" : countLabel;
  const handleView = comingSoonShown ? (onViewComingSoon ?? onView) : onView;
  const singular = shownNoun.endsWith("s") ? shownNoun.slice(0, -1) : shownNoun;
  // Loader shows only while the visible set is cold-loading with nothing to
  // draw — once pins exist (from cache or a fast answer) the map wins.
  const shownLoading = (comingSoonShown ? comingSoonLoading : loading) && shownPoints.length === 0;

  const switchTab = (tab: "active" | "coming_soon") => {
    setMapTab(tab);
    setQuery(""); // panel search applies to the visible set only
  };

  const marketCount = useMemo(() => {
    const c = new Set<string>();
    for (const p of shownPoints) {
      const a = abbrOf(p);
      if (a) c.add(a);
    }
    return c.size;
  }, [shownPoints]);

  // Panel heading: the searched state's name when the query names one exactly,
  // otherwise the whole market.
  const marketTitle = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      const state = Object.keys(NAME_TO_ABBR).find((n) => n.toLowerCase() === q);
      if (state) return state;
    }
    return "United States";
  }, [query]);

  // Keyword search over the listings panel — matches title, city, or state name.
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return shownPoints;
    return shownPoints.filter((p) =>
      [p.title, p.city_name, p.state_name]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q)),
    );
  }, [shownPoints, query]);

  return (
    <div className="public-listing-card rounded-[1.5rem] border border-border/60 p-5 sm:p-6">
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Explore the market
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {shownLoading
              ? `Scanning the market for ${shownNoun}…`
              : `Tap a pin to view · ${shownPoints.length.toLocaleString()} ${shownNoun} across ${marketCount} markets`}
          </p>
        </div>

        {/* Active / Coming Soon pin-set toggle */}
        {showToggle && (
          <div className="inline-flex items-center rounded-full border border-border/60 bg-muted p-1">
            <button
              type="button"
              onClick={() => switchTab("active")}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                mapTab === "active" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Active
              <span className="ml-1.5 opacity-75">{loading && points.length === 0 ? "…" : points.length.toLocaleString()}</span>
            </button>
            <button
              type="button"
              onClick={() => switchTab("coming_soon")}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                mapTab === "coming_soon" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Coming Soon
              <span className="ml-1.5 opacity-75">{comingSoonLoading && comingSoonPoints!.length === 0 ? "…" : comingSoonPoints!.length.toLocaleString()}</span>
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        {/* Left: interactive US map (Leaflet + street tiles, grouped listing pins) */}
        <div className="relative min-w-0 overflow-hidden rounded-2xl border border-border/50 bg-muted p-4">
          <div className="mb-3">
            <span className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              {shownNoun} map
            </span>
          </div>

          <div className="relative h-[26rem] sm:h-[30rem]">
            {/* resetToken clears the map's search/highlight state on toggle so
                it never leaks between the Active and Coming Soon pin sets —
                without remounting Leaflet, which would re-download every tile */}
            <ListingsMap points={shownPoints} onView={handleView} resetToken={mapTab} />
            {/* Cold-load veil: dims the empty map (and its "0 businesses" chip)
                until the pins arrive. z-index sits above Leaflet's overlays. */}
            {shownLoading && (
              <div className="absolute inset-0 z-[1300] flex items-center justify-center rounded-[1.1rem] bg-background/55 backdrop-blur-[2px]">
                <MarketLoader label={`Placing ${shownNoun} on the map…`} />
              </div>
            )}
          </div>
        </div>

        {/* Right: markets panel */}
        <div className="flex min-w-0 flex-col rounded-2xl border border-border/50 bg-muted p-4">
          <div className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            All markets
          </div>
          <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-foreground">
            {marketTitle}
          </h3>
          <div className="mt-0.5">
            <span className="text-xl font-black tracking-tight text-primary">
              {shownLoading ? "…" : searched.length.toLocaleString()}
            </span>
            <span className="ml-1.5 text-sm text-muted-foreground">
              {shownLoading ? "loading" : query.trim() ? `matching ${searched.length === 1 ? singular : shownNoun}` : shownCountLabel}
            </span>
          </div>

          {/* Search within the current market */}
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${shownNoun} by name or location…`}
              className="h-10 w-full rounded-xl border border-border/60 bg-muted pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="mt-4 max-h-[22rem] space-y-2 overflow-y-auto pr-1">
            {shownLoading ? (
              <MarketLoader label={`Finding ${shownNoun}…`} />
            ) : searched.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border/60 px-4 py-8 text-center text-sm text-muted-foreground">
                {query.trim() ? `No ${shownNoun} match your search.` : `No ${shownNoun} in this market yet.`}
              </div>
            ) : (
              searched.slice(0, 80).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleView(p.id)}
                  className="flex w-full items-center gap-3 rounded-xl border border-border/50 bg-muted p-3 text-left transition-colors hover:border-primary/40 hover:bg-accent"
                >
                  {p.listing_image_url ? (
                    <img
                      src={assetUrl(p.listing_image_url)!}
                      alt={p.title}
                      loading="lazy"
                      className="h-11 w-11 shrink-0 rounded-lg border border-border/50 object-cover"
                    />
                  ) : (
                    <span
                      className="h-11 w-11 shrink-0 rounded-lg border border-border/50"
                      style={{ background: "linear-gradient(135deg, hsl(40 20% 16%), hsl(40 16% 10%))" }}
                    />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-foreground">{p.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[
                        p.category_name,
                        [p.city_name, p.state_name ? NAME_TO_ABBR[p.state_name] ?? p.state_name : null]
                          .filter(Boolean)
                          .join(", "),
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-bold text-primary">{fmtPrice(p.asking_price)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
